import React, { useEffect, useRef, useState, useCallback } from "react";
import { Loader2, MapPin, Camera, RefreshCw } from "lucide-react";

import { useToast } from "src/components/ui/toast";
import { useAuth } from "src/contexts/AuthContext";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "src/components/ui/dialog";
import { fetchToday, markAttendance } from "src/features/attendance/api/attendanceApi";
import { matchGeofence, nearestGeofence } from "src/features/attendance/utils/geo";

const QUOTE = "Attendance is the first step to start with.";

function AnalogClock({ date }) {
  const seconds = date.getSeconds();
  const minutes = date.getMinutes();
  const hours = date.getHours() % 12;

  const secondAngle = seconds * 6;
  const minuteAngle = minutes * 6 + seconds * 0.1;
  const hourAngle = hours * 30 + minutes * 0.5;

  const hand = (angle, length, width, color) => {
    const rad = ((angle - 90) * Math.PI) / 180;
    const x = 50 + length * Math.cos(rad);
    const y = 50 + length * Math.sin(rad);
    return (
      <line
        x1="50"
        y1="50"
        x2={x}
        y2={y}
        stroke={color}
        strokeWidth={width}
        strokeLinecap="round"
      />
    );
  };

  return (
    <svg viewBox="0 0 100 100" className="h-20 w-20">
      <circle cx="50" cy="50" r="48" fill="#0f172a" />
      <circle cx="50" cy="50" r="48" fill="none" stroke="#1e293b" strokeWidth="3" />
      {Array.from({ length: 12 }).map((_, i) => {
        const rad = ((i * 30 - 90) * Math.PI) / 180;
        const x1 = 50 + 42 * Math.cos(rad);
        const y1 = 50 + 42 * Math.sin(rad);
        const x2 = 50 + 46 * Math.cos(rad);
        const y2 = 50 + 46 * Math.sin(rad);
        return (
          <line key={i} x1={x1} y1={y1} x2={x2} y2={y2} stroke="#475569" strokeWidth="1.5" />
        );
      })}
      {hand(hourAngle, 24, 3, "#e2e8f0")}
      {hand(minuteAngle, 34, 2.5, "#e2e8f0")}
      {hand(secondAngle, 38, 1.5, "#4f39f6")}
      <circle cx="50" cy="50" r="3" fill="#4f39f6" />
    </svg>
  );
}

export function AttendanceClockWidget() {
  const { toast } = useToast();
  const { can } = useAuth();
  const [now, setNow] = useState(new Date());
  const [today, setToday] = useState(null);
  const [geofences, setGeofences] = useState(null); // null = not loaded yet
  const [position, setPosition] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [captureOpen, setCaptureOpen] = useState(false);
  const [selfie, setSelfie] = useState(null); // { blob, url }
  const [cameraReady, setCameraReady] = useState(false);
  const loadedRef = useRef(false);
  const videoRef = useRef(null);
  const streamRef = useRef(null);

  const canMark = can("attendance", "mark");

  // Tick the clock every second.
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(id);
  }, []);

  const loadToday = useCallback(async () => {
    try {
      const data = await fetchToday();
      setToday(data);
      setGeofences(data.geofences || []);
    } catch {
      setGeofences([]);
    }
  }, []);

  useEffect(() => {
    if (!canMark || loadedRef.current) return;
    loadedRef.current = true;
    loadToday();
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (pos) =>
          setPosition({
            lat: pos.coords.latitude,
            lng: pos.coords.longitude,
            accuracy: pos.coords.accuracy,
          }),
        () => setPosition(null),
        { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 }
      );
    }
  }, [canMark, loadToday]);

  const nextKind = today?.check_in ? "check_out" : "check_in";
  const alreadyDone = today?.check_in && today?.check_out;
  const noGeofences = geofences !== null && geofences.length === 0;
  const inside = position && geofences ? matchGeofence(position.lat, position.lng, geofences) : null;
  const nearest = position && geofences ? nearestGeofence(position.lat, position.lng, geofences) : null;

  const digital = now.toLocaleTimeString([], {
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
  const dateStr = now.toLocaleDateString([], { day: "numeric", month: "short", year: "numeric" });

  const stopCamera = useCallback(() => {
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    setCameraReady(false);
  }, []);

  const startCamera = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "user" },
        audio: false,
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
      }
      setCameraReady(true);
    } catch {
      setCameraReady(false);
      toast({ variant: "destructive", title: "Camera", description: "Couldn't access the camera." });
    }
  }, [toast]);

  function openCapture() {
    if (!position) {
      toast({ variant: "destructive", title: "Location needed", description: "Enable location to clock in." });
      return;
    }
    setSelfie(null);
    setCaptureOpen(true);
    startCamera();
  }

  function closeCapture() {
    setCaptureOpen(false);
    stopCamera();
    if (selfie?.url) URL.revokeObjectURL(selfie.url);
    setSelfie(null);
  }

  function capture() {
    const video = videoRef.current;
    if (!video) return;
    const canvas = document.createElement("canvas");
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    canvas.getContext("2d").drawImage(video, 0, 0);
    canvas.toBlob(
      (blob) => {
        if (blob) {
          setSelfie({ blob, url: URL.createObjectURL(blob) });
          stopCamera();
        }
      },
      "image/jpeg",
      0.85
    );
  }

  function retake() {
    if (selfie?.url) URL.revokeObjectURL(selfie.url);
    setSelfie(null);
    startCamera();
  }

  async function submit() {
    if (!position || !selfie) return;
    setSubmitting(true);
    try {
      await markAttendance({
        kind: nextKind,
        latitude: position.lat,
        longitude: position.lng,
        accuracyMeters: position.accuracy,
        selfieBlob: selfie.blob,
      });
      toast({ title: "Clocked in", description: `Your ${nextKind.replace("_", "-")} is recorded.` });
      closeCapture();
      await loadToday();
    } catch (error) {
      const data = error.response?.data;
      toast({
        variant: "destructive",
        title: data?.status === "rejected" ? "Out of range" : "Couldn't clock in",
        description: data?.message || "Something went wrong.",
      });
    } finally {
      setSubmitting(false);
    }
  }

  if (!canMark) return null;

  const buttonLabel = alreadyDone
    ? "Done for today"
    : nextKind === "check_in"
      ? "Clock In"
      : "Clock Out";

  return (
    <div className="flex flex-col items-center rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
      <AnalogClock date={now} />

      <div className="mt-2 text-center">
        <div className="text-xl font-bold tracking-tight text-slate-900">{digital}</div>
        <div className="text-[11px] text-slate-400">{dateStr}</div>
      </div>

      <p className="mt-2 max-w-[180px] text-center text-[11px] italic leading-snug text-slate-500">
        “{QUOTE}”
      </p>

      {noGeofences ? (
        <div className="mt-3 flex items-center gap-1.5 rounded-md bg-slate-50 px-2.5 py-1.5 text-center text-[11px] text-slate-500">
          <MapPin className="h-3.5 w-3.5 shrink-0" />
          Contact admin for enabling this feature.
        </div>
      ) : (
        <>
          {position && !alreadyDone && (
            <p
              className={`mt-2 text-center text-[11px] ${
                inside ? "text-emerald-600" : "text-rose-500"
              }`}
            >
              {inside
                ? `Within “${inside.geofence.name}”`
                : nearest
                  ? `${Math.round(nearest.distance)} m from “${nearest.geofence.name}”`
                  : ""}
            </p>
          )}
          <button
            onClick={openCapture}
            disabled={submitting || alreadyDone || !inside}
            className="mt-3 flex w-full items-center justify-center gap-1.5 rounded-lg bg-emerald-500 px-4 py-2 text-[13px] font-semibold text-white transition-colors hover:bg-emerald-600 disabled:cursor-not-allowed disabled:bg-slate-200 disabled:text-slate-400"
          >
            {buttonLabel}
          </button>
        </>
      )}

      {/* Selfie capture modal */}
      <Dialog open={captureOpen} onOpenChange={(open) => (open ? null : closeCapture())}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              Take a selfie to {nextKind === "check_in" ? "clock in" : "clock out"}
            </DialogTitle>
          </DialogHeader>

          <div className="aspect-[4/3] w-full min-w-0 overflow-hidden rounded-lg border border-slate-200 bg-slate-100">
            {selfie ? (
              <img src={selfie.url} alt="Attendance selfie" className="h-full w-full object-cover" />
            ) : (
              <video ref={videoRef} className="h-full w-full object-cover" muted playsInline />
            )}
          </div>

          {inside && (
            <p className="text-center text-[12px] text-emerald-600">
              Within “{inside.geofence.name}”
            </p>
          )}

          <div className="flex gap-2">
            {selfie ? (
              <>
                <button
                  onClick={retake}
                  className="flex flex-1 items-center justify-center gap-1.5 rounded-lg border border-slate-300 px-4 py-2.5 text-sm font-medium text-slate-700 hover:bg-slate-50"
                >
                  <RefreshCw className="h-4 w-4" />
                  Retake
                </button>
                <button
                  onClick={submit}
                  disabled={submitting}
                  className="flex flex-1 items-center justify-center gap-1.5 rounded-lg bg-emerald-500 px-4 py-2.5 text-sm font-semibold text-white hover:bg-emerald-600 disabled:bg-slate-200 disabled:text-slate-400"
                >
                  {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
                  {nextKind === "check_in" ? "Clock In" : "Clock Out"}
                </button>
              </>
            ) : (
              <button
                onClick={capture}
                disabled={!cameraReady}
                className="flex flex-1 items-center justify-center gap-1.5 rounded-lg bg-[#4f39f6] px-4 py-2.5 text-sm font-semibold text-white hover:bg-[#4332d6] disabled:bg-slate-200 disabled:text-slate-400"
              >
                <Camera className="h-4 w-4" />
                Capture
              </button>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
